import { useEffect, useReducer, useState } from 'react';
import {
  ADD_TEMPLATES,
  AVAILABLE_TEMPLATES
} from '../../../shared/AppConstants';
import { useUserInfo } from '../../../shared/UserInfo';
import {
  fetchJsonWithAuth,
  fetchWithAuth
} from '../../../shared/fetch-json.util';
import SMModal from '../../../shared/SMModal';
import { callUserInfoAPI } from '../../../shared/APIHelper';
import { FileUploadCard } from '../../../shared/FileUploadCard';
import { EditEntityModal } from '../../../shared/EditEntityModal';
import { FileUpload } from '../../../shared/FileUpload';
import { EntityLinksList } from '../../../shared/EntityLinksList';
import {
  saveFileUpload,
  sortedTemplateList
} from '../../../shared/UtilFunctions';
import { Divider, Typography } from '@mui/material';
import { useToastProvider } from '../../../shared/ToastProvider';
import { getEnvVar } from '../../../utils/getEnvVar';

const initialState = {
  loading: true,
  error: '',
  post: []
};

export function StrategyTemplates({ commonProps }) {
  const info = useUserInfo();
  const tokenAvailable = info?.token;
  const [templateToEdit, setTemplateToEdit] = useState(null);
  const [showModalEdit, setShowModalEdit] = useState(false);
  const [showModalDelete, setShowModalDelete] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const handleModalDeleteCancel = () => setShowModalDelete(false);
  const [userInfoMap, setUserInfoMap] = useState(new Map());
  const [templateData, setTemplateData] = useState([]);
  const {
    setStatusErrorMsg,
    incrementAPIInProgress,
    decrementAPIInProgress,
    clearAllMessages,
    setStatusSuccessMsg
  } = commonProps;
  const { enqueueToast } = useToastProvider();

  function populateOtherInfo(current, actionType) {
    if (setTemplateData && actionType !== 'FETCH_SUCCESS')
      setTemplateData(current);

    return current;
  }

  const reducer = (state, action) => {
    switch (action.type) {
      case 'FETCH_SUCCESS':
        return {
          loading: false,
          post: populateOtherInfo(action.payload),
          error: ''
        };
      case 'FETCH_FAIL':
        return {
          loading: false,
          post: [],
          error: 'Something went wrong!'
        };
      case 'add': {
        let objArray = [...state.post].concat(action.payload);

        return {
          loading: false,
          post: populateOtherInfo(objArray, action.payload),
          error: ''
        };
      }
      case 'edit': {
        let objArray = [...state.post];

        // Find the edited item index
        let index = objArray.findIndex(
          (obj) => obj.fileName === action.payload.fileName
        );

        objArray[index] = action.payload;

        return {
          loading: false,
          post: populateOtherInfo(objArray, action.type),
          error: ''
        };
      }
      case 'del': {
        let objArray = [...state.post];

        let indexToDelete = objArray.findIndex(
          (obj) => obj?.templateId === templateToDelete.templateId
        );
        objArray.splice(indexToDelete, 1);

        return {
          loading: false,
          post: populateOtherInfo(objArray),
          error: ''
        };
      }

      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const handleModalEditCancel = () => {
    setShowModalEdit(false);
  };

  function handleModalDeleteYes(e) {
    e.preventDefault();
    setShowModalDelete(false);
    clearAllMessages();

    incrementAPIInProgress();
    fetchWithAuth(
      getEnvVar('VITE_TEMPLATE_API_URL') + '/' + templateToDelete.templateId,
      {
        method: 'DELETE'
      }
    )
      .then((response) => {
        if (response.status === 200) {
          setStatusSuccessMsg('Template has been successfully deleted.');
          dispatch({ type: 'del' });
        } else if (response.status === 400 || response.status === 404)
          setStatusErrorMsg('Bad Request!');
      })
      .catch((error) =>
        setStatusErrorMsg(
          "Couldn't delete the Template. Please try again.",
          error
        )
      )
      .finally(() => decrementAPIInProgress());
  }

  function callTemplate() {
    incrementAPIInProgress();
    fetchJsonWithAuth(getEnvVar('VITE_TEMPLATE_API_URL'))
      .then((response) => {
        const unsortedList = response.json;
        const sortedList = sortedTemplateList(unsortedList);
        dispatch({ type: 'FETCH_SUCCESS', payload: sortedList });
      })
      .catch((error) =>
        setStatusErrorMsg(
          "Couldn't load the Template. Please try again.",
          error
        )
      )
      .finally(() => decrementAPIInProgress());
  }

  const onSave = (data, event, clearComponent) => {
    const { description, selectedPhases, variableFormContent } = data;

    saveFileUpload(
      event,
      'template',
      variableFormContent,
      description,
      getEnvVar('VITE_TEMPLATE_API_URL'),
      dispatch,
      clearComponent,
      selectedPhases,
      commonProps,
      enqueueToast
    );
  };

  useEffect(() => {
    if (tokenAvailable) callTemplate();
  }, [tokenAvailable]);

  useEffect(() => {
    const userList = new Set();
    const templateList = state.post;

    setTemplateData(templateList);
    templateList?.forEach((item) => {
      if (item.createdById) userList.add(item.createdById);
      if (item.updatedById) userList.add(item.updatedById);
    });

    if (userList.size > 0)
      callUserInfoAPI(
        Array.from(userList),
        setUserInfoMap,
        null,
        incrementAPIInProgress,
        decrementAPIInProgress,
        setStatusErrorMsg,
        true
      );
  }, [state.post]);

  return (
    <>
      <FileUploadCard
        title={ADD_TEMPLATES}
        entity={'template'}
        onSave={onSave}
        commonProps={commonProps}
        additionalContent={
          <>
            <Divider sx={{ my: 1.5 }} />
            {templateData?.length > 0 && (
              <Typography variant={'h2'} style={{ paddingTop: '0.5em' }}>
                {AVAILABLE_TEMPLATES}
              </Typography>
            )}
            <EntityLinksList
              key={templateData}
              entityName={'Template'}
              apiUrl={`${getEnvVar('VITE_TEMPLATE_API_URL')}`}
              setEntityData={setTemplateData}
              entityData={templateData}
              userInfoMap={userInfoMap}
              isEdit={true}
              commonProps={commonProps}
              dispatch={dispatch}
              entityToDelete={templateToDelete}
              setEntityToDelete={setTemplateToDelete}
              enqueueToast={enqueueToast}
            />
          </>
        }
        VariableForm={FileUpload}
      />
      <SMModal
        showModal={showModalDelete}
        handleCancel={handleModalDeleteCancel}
        handleYes={handleModalDeleteYes}
        title="Delete Template?"
        msg_line1="Are you sure you want to delete this Template?"
        msg_line2=""
        cancelText="No"
        yesText="Yes"
        id={templateToDelete}
      />
      {showModalEdit && (
        <EditEntityModal
          showModal={showModalEdit}
          handleCancel={handleModalEditCancel}
          entityToEdit={templateToEdit}
          dispatch={dispatch}
          setEntityToEdit={setTemplateToEdit}
          entityName={'Template'}
          url={
            getEnvVar('VITE_TEMPLATE_API_URL') + '/' + templateToEdit.templateId
          }
        />
      )}
    </>
  );
}
